/**
 * "What the generator read" — the RECORDED generation context (PR3).
 *
 * Every figure on this panel was parsed out of the stored S3 prompt, so it is a record of
 * what the page writer actually saw, not a reconstruction from today's database. That
 * distinction is the point: "this attribute was in the prompt" and "this attribute is in
 * the knowledge base now" are different claims, and the gap between them is the finding.
 *
 * Loads lazily — the endpoint costs an S3 round trip, so it is fetched only when the
 * reader opens the Generate step.
 */
import React, { useEffect, useState } from 'react';
import {
  fetchGenerationContext, type GenerationContext as Ctx, type NumberAudit,
} from '@/services/productCockpitService';
import { fmtInt } from '../lib/stages';

const Syn = ({ children }: { children: React.ReactNode }) => (
  <span className="pmc-syn" data-tip="computed by the cockpit — not stored in the database">{children}</span>
);

/** The deterministic number audit, stated as a candidate rather than a verdict. */
export function NumberAuditLine({ audit }: { audit: NumberAudit }) {
  return (
    <div className="pmc-small pmc-numaudit">
      <Syn>
        {`number audit (deterministic): ${audit.sourced} of ${audit.numbers_in_response} numbers in the recorded response are sourced from their own prompt`}
        {audit.self_scores_by_design > 0
          ? ` · ${audit.self_scores_by_design} are the model scoring itself (fit_score/confidence — by design)`
          : ''}
        {` · ${audit.unsourced.length} unsourced candidate${audit.unsourced.length === 1 ? '' : 's'}`}
      </Syn>
    </div>
  );
}

export function UnsourcedList({ audit }: { audit: NumberAudit }) {
  if (!audit.unsourced.length) return null;
  return (
    <details className="pmc-sprompt" open>
      <summary>
        <Syn>{`unsourced numbers — hallucination candidates (${audit.unsourced.length})`}</Syn>
      </summary>
      <div className="pmc-small pmc-unsourced">
        {audit.unsourced.map((u, i) => (
          <div className="pmc-srow" key={`${u.value}-${i}`}>
            <div className="pmc-srow-main">
              <span className="pmc-sk">
                {u.value}{u.unit ? ` ${u.unit}` : ''}{u.kind === 'measurement' ? '' : ' (count)'}
              </span>
              <span className="pmc-sval">…{u.excerpt}…</span>
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

/** The deterministic structure map of the FULL prompt, block by block. */
export function PromptStructure({ section }: { section: Ctx['sections'][number] }) {
  const blocks = section.prompt_blocks;
  if (!blocks?.length) {
    return <div className="pmc-small pmc-mute">prompt structure not available for this section</div>;
  }
  return (
    <details className="pmc-sprompt">
      <summary>
        {`how this section was built — prompt structure (${blocks.length} blocks · ${fmtInt(section.prompt_chars)} chars)`}
      </summary>
      <div className="pmc-pblocks">
        {blocks.map((b, i) => (
          <details className="pmc-pblock" key={`${b.heading}-${i}`}>
            <summary>
              <span className="pmc-mono pmc-small">{b.heading}</span>
              <span className="pmc-small pmc-mute pmc-push">{fmtInt(b.chars)} ch</span>
            </summary>
            <pre className="pmc-sprompt-pre">
              {b.excerpt}{b.chars > 400 ? '\n… (truncated — the full prompt lives in S3)' : ''}
            </pre>
          </details>
        ))}
      </div>
    </details>
  );
}

interface GenerationContextPanelProps {
  siteId: string;
  productRef: string;
  /** lifted so the Generate step can hang per-section audits off the same fetch */
  onLoaded?: (ctx: Ctx | null) => void;
}

export function GenerationContextPanel({ siteId, productRef, onLoaded }: GenerationContextPanelProps) {
  const [ctx, setCtx] = useState<Ctx | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    fetchGenerationContext(siteId, productRef, ac.signal)
      .then((d) => { setCtx(d); setLoading(false); onLoaded?.(d); })
      .catch((err) => {
        if ((err as { name?: string })?.name === 'AbortError') return;
        setError(err?.message ?? String(err));
        setLoading(false);
        onLoaded?.(null);
      });
    return () => ac.abort();
    // onLoaded is a render-stable callback from the parent; re-fetching on it would loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, productRef]);

  if (loading) {
    return <div className="pmc-notyet">Reading the recorded prompts from S3…</div>;
  }
  if (error) {
    return (
      <div className="pmc-error" style={{ marginBottom: 14 }}>
        <div className="pmc-error-title">The recorded generation context could not be read</div>
        <div className="pmc-error-msg">{error}</div>
      </div>
    );
  }
  if (!ctx) return null;

  // An absence is a finding, and WHICH absence matters — say it, never show an empty panel.
  if (!ctx.s3_retrieved || !ctx.prompt_context) {
    return (
      <div className="pmc-notyet">
        <b>What the generator read</b> — not available for this product.
        <div style={{ marginTop: 4 }}>
          {ctx.unavailable_reason ?? 'the stored prompt carried no parseable KEY ATTRIBUTES block'}
        </div>
        {ctx.prompts_s3_key && (
          <div className="pmc-small pmc-mute" style={{ marginTop: 4 }}>
            s3 key: <span className="pmc-mono">{ctx.prompts_s3_key}</span>
          </div>
        )}
      </div>
    );
  }

  const c = ctx.prompt_context;
  // ⛔ EITHER attribute block being unreadable withholds the same two results.
  // `not_reached` is computed from BOTH blocks — a key is "not reached" only when it is in
  // neither — and `drifted` compares against whichever block carried the key. So an
  // unreadable UNVERIFIED block withholds them exactly as an unreadable authoritative one
  // does, and gating the unknown-counts on the authoritative error alone left the summary
  // printing "0 … were NOT · 0 changed" directly above a warning that said those results
  // could not be computed. The summary and the warning must never disagree.
  const blockErrors = [c.authoritative_parse_error, c.unverified_parse_error].filter(Boolean) as string[];
  const blockUnreadable = blockErrors.length > 0;

  return (
    <div className="pmc-ctxcard">
      <div className="pmc-ctxhd">
        <span className="pmc-ctxkey">What the generator read</span>
        <span
          className="pmc-chipv pmc-recorded"
          data-tip={`recorded — parsed deterministically from the stored S3 generation prompt${c.source_section ? ` (section '${c.source_section}')` : ''}, NOT reconstructed from today's data.`}
        >
          recorded
        </span>
      </div>

      <div className="pmc-small" style={{ marginTop: 6 }}>
        enrichment version seen at generation:{' '}
        <span className="pmc-mono">{String(c.last_enriched_at_generation ?? 'not stated').slice(0, 19)}</span>
      </div>

      {/*
        ⛔ When the authoritative block is unreadable the backend returns an EMPTY
        `injected_attributes` and WITHHOLDS `not_reached` — neither is a measurement. Printing
        their lengths turned that into "0 attributes were in the prompt · 0 … were NOT ·
        0 changed", a reassuring, entirely fabricated clean bill over evidence nobody could
        read. The counts that depend on the unreadable block are marked unknown instead, and
        `reached` is still shown because it is real: those keys were found in the readable
        UNVERIFIED block.
      */}
      <div className="pmc-small" style={{ marginTop: 4 }}>
        <Syn>
          {blockUnreadable
            ? `${c.reached.length} attributes were found in the readable part of the prompt · how many of today's knowledge base were NOT is UNKNOWN · how many changed value is UNKNOWN`
            : `${c.reached.length} attributes were in the prompt · ${c.not_reached.length} in today's knowledge base were NOT · ${c.drifted.length} changed value since`}
        </Syn>
      </div>

      {blockErrors.map((err) => (
        <div className="pmc-small pmc-unreadable" key={err}>
          <Syn>{`⚠ ${err}. The counts above and the lists below cover only what could be read — nothing here may be read as "the generator never received it".`}</Syn>
        </div>
      ))}

      {ctx.number_audit_summary && (
        <NumberAuditLine
          audit={{
            checked: true,
            numbers_in_response: ctx.number_audit_summary.numbers_in_responses,
            sourced: ctx.number_audit_summary.sourced,
            self_scores_by_design: ctx.number_audit_summary.self_scores_by_design,
            unsourced: new Array(ctx.number_audit_summary.unsourced).fill(null).map(() => ({
              value: '', unit: null, kind: 'count' as const, excerpt: '',
            })),
          }}
        />
      )}

      {!!c.reached_unverified?.length && (
        <details className="pmc-sprompt">
          <summary>{`in the prompt, but under NOT AUTHORITATIVE (${c.reached_unverified.length})`}</summary>
          <div className="pmc-small" style={{ padding: '6px 0' }}>
            The generator was told it may use these as soft context and must never cite them
            as measured fact.
          </div>
          <div className="pmc-gpills">
            {c.reached_unverified.map((k) => <span className="pmc-gpill" key={k}>{k}</span>)}
          </div>
        </details>
      )}

      {c.not_reached.length > 0 && (
        <details className="pmc-sprompt">
          <summary>{`in the knowledge base today but NOT in the prompt (${c.not_reached.length})`}</summary>
          <div className="pmc-gpills" style={{ marginTop: 6 }}>
            {c.not_reached.map((k) => <span className="pmc-gpill" key={k}>{k}</span>)}
          </div>
        </details>
      )}

      {c.drifted.length > 0 && (
        <details className="pmc-sprompt" open>
          <summary>{`values that drifted since generation (${c.drifted.length})`}</summary>
          <div className="pmc-small" style={{ padding: '6px 0' }}>
            {c.drifted.map((d) => (
              <div className="pmc-srow" key={d.key}>
                <div className="pmc-srow-main">
                  <span className="pmc-sk">{d.key}</span>
                  <span className="pmc-sval">
                    prompt: {String(d.prompt_value)} → today: {String(d.db_value_today)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* unreadable evidence must never look like an absence — the judge penalises the
          second, and a human reading this panel would draw the same wrong conclusion */}
      {c.benefits_parse_error && (
        <div className="pmc-small pmc-unreadable">
          <Syn>{`⚠ the archived benefit scores could not be read — ${c.benefits_parse_error}. This is unreadable evidence, not an absence of injected benefits.`}</Syn>
        </div>
      )}

      {c.benefits_injected.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="pmc-sgroup-label">benefits injected at generation time (with AI scores · highest first · bar = score out of 100)</div>
          <BenefitBars benefits={c.benefits_injected} />
        </div>
      )}

      {c.grounded_specs.length > 0 && (
        <details className="pmc-sprompt">
          <summary>{`grounded spec values in the prompt (${c.grounded_specs.length})`}</summary>
          <div className="pmc-small" style={{ padding: '6px 0' }}>
            {c.grounded_specs.map((g, i) => (
              <div className="pmc-srow" key={`${g.name}-${i}`}>
                <div className="pmc-srow-main">
                  <span className="pmc-sk">{g.name}</span>
                  <span className="pmc-sval">{g.value}</span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {c.persona_excerpt && (
        <details className="pmc-sprompt">
          <summary>persona &amp; tone guidance (excerpt)</summary>
          <pre className="pmc-sprompt-pre">{c.persona_excerpt}</pre>
        </details>
      )}
    </div>
  );
}

type InjectedBenefit = { name: string; score: number | null; explanation?: string };

/** a score the bar can draw: a finite number on the enrichment's 0–100 scale */
const onScale = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100;

/**
 * Highest score first; a benefit with no readable score goes LAST — it is not a zero, so it must
 * not sort among the zeros. Ties keep a stable, readable order (by name).
 */
export function sortBenefits<T extends { name: string; score: number | null }>(benefits: T[]): T[] {
  const n = (b: T) => (typeof b.score === 'number' && Number.isFinite(b.score) ? b.score : null);
  return [...benefits].sort((a, b) => {
    const x = n(a);
    const y = n(b);
    if (x === null || y === null) return x === y ? a.name.localeCompare(b.name) : x === null ? 1 : -1;
    return y - x || a.name.localeCompare(b.name);
  });
}

/**
 * The injected benefits as bars (Jaume, 2026-09-25: "visually a bar and sorted from more to
 * less"). The scale is the enrichment's own 0–100 (db2: 150,820 scores over 20,000 products, all
 * integers in [0, 100]). A value off that scale is shown as its number with NO bar — clamping it
 * would draw a measurement nobody made.
 */
function BenefitBars({ benefits }: { benefits: InjectedBenefit[] }) {
  return (
    <div className="pmc-bbars" role="list">
      {sortBenefits(benefits).map((b) => (
        <div className="pmc-bbar-row" role="listitem" key={b.name}>
          <span className="pmc-sk pmc-bbar-name" title={b.explanation || undefined}>{b.name}</span>
          <span className="pmc-bbar-track" aria-hidden="true">
            {onScale(b.score) && <span className="pmc-bbar-fill" style={{ width: `${b.score}%` }} />}
          </span>
          <span className="pmc-bbar-val">
            {b.score == null ? '—' : onScale(b.score) ? b.score : <Syn>{`${b.score} (off the 0–100 scale)`}</Syn>}
          </span>
        </div>
      ))}
    </div>
  );
}
